import { Add } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import { usePageTitle } from "app/hooks/usePageTitle";
import { useTranslationsReady } from "app/providers/TranslationProvider";
import { useTables } from "app/hooks/useTables";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import queryKeys from "shared/services/queryKeys";
import { useFormTitleTranslations } from "../services/formTitle";

interface FormListRecord {
    id: string;
    title?: string | null;
    name?: string | null;
    category_id?: string | null;
    target_table?: string | null;
    active?: boolean | null;
    published?: boolean | null;
}

const ServiceFormListPage = () => {
    const { t } = useTranslation();
    const translationsReady = useTranslationsReady();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("title_asc");
    const tablesQuery = useTables();
    const targetTableLabel = (name?: string | null) => {
        if (!name) return "—";
        const table = tablesQuery.data.find((entry) => entry.name === name);
        return table ? translateMetadataLabel(t, table) : name;
    };
    usePageTitle(String(t("Service Builder")), { enabled: translationsReady });
    const listQuery = useQuery({
        queryKey: queryKeys.records.list("sys_sp_form_catalog", "list", {}, { field: "title", direction: "asc" }),
        queryFn: async () => {
            const response = await new RecordManagement<FormListRecord>("sys_sp_form_catalog").run();
            const records = response?.data || (response as any)?.records || response;
            return Array.isArray(records) ? records as FormListRecord[] : [];
        },
    });
    const categoriesQuery = useQuery({
        queryKey: queryKeys.records.lists("sys_sp_categories"),
        queryFn: async () => {
            const response = await new RecordManagement<{ id: string; title?: string | null; name?: string | null }>("sys_sp_categories").run();
            const records = response?.data || (response as any)?.records || response;
            return Array.isArray(records) ? records : [];
        },
    });
    const formTitle = useFormTitleTranslations((listQuery.data || []).map((record) => record.title || ""));
    const categoryTitle = (id?: string | null) => {
        if (!id) return "—";
        const category = categoriesQuery.data?.find((entry) => entry.id === id);
        return category ? t(category.title || category.name || id) : "—";
    };
    const rows = useMemo(() => {
        const term = search.trim().toLocaleLowerCase();
        return [...(listQuery.data || [])]
            .filter((record) => !term || [formTitle(record.title, record.name), categoryTitle(record.category_id), targetTableLabel(record.target_table)].some((value) => String(value || "").toLocaleLowerCase().includes(term)))
            .sort((a, b) => {
                const comparison = formTitle(a.title, a.name).localeCompare(formTitle(b.title, b.name));
                return sort === "title_desc" ? -comparison : comparison;
            });
    }, [listQuery.data, categoriesQuery.data, tablesQuery.data, search, sort, t, formTitle]);

    return <Box sx={{ p: 3, width: "100%" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h5">{t("Service Builder")}</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/home/service-builder/new")}>{t("New form")}</Button>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField size="small" label={t("Search forms")} value={search} onChange={(event) => setSearch(event.target.value)} />
            <FormControl size="small" sx={{ minWidth: 180 }}><InputLabel>{t("Sort by")}</InputLabel>
                <Select label={t("Sort by")} value={sort} onChange={(event) => setSort(event.target.value)}>
                    <MenuItem value="title_asc">{t("Title A–Z")}</MenuItem>
                    <MenuItem value="title_desc">{t("Title Z–A")}</MenuItem>
                </Select>
            </FormControl>
        </Stack>
        {listQuery.isLoading ? <CircularProgress size={24} /> : listQuery.isError ? <Alert severity="error">{t("Unable to load service forms.")}</Alert> : rows.length === 0 ?
            <Typography color="text.secondary">{t(search ? "No matching forms." : "No service forms yet.")}</Typography> :
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { p: 1.5, textAlign: "left", borderBottom: "1px solid", borderColor: "divider" }, "& tbody tr": { cursor: "pointer" }, "& tbody tr:hover": { bgcolor: "action.hover" } }}>
                <thead><tr><th>{t("Title")}</th><th>{t("Category")}</th><th>{t("Target table")}</th><th>{t("Active")}</th><th>{t("Published")}</th></tr></thead>
                <tbody>{rows.map((row) => <tr key={row.id} tabIndex={0} onClick={() => navigate(`/home/service-builder/${row.id}`)} onKeyDown={(event) => { if (event.key === "Enter") navigate(`/home/service-builder/${row.id}`); }}>
                    <td>{formTitle(row.title, row.name)}</td><td>{categoryTitle(row.category_id)}</td><td>{targetTableLabel(row.target_table)}</td><td>{t(row.active ? "Yes" : "No")}</td><td>{t(row.published ? "Yes" : "No")}</td>
                </tr>)}</tbody>
            </Box>}
    </Box>;
};
export default ServiceFormListPage;
